/**
 * 自動応答の API 形 ⇄ 画面の下書き形の変換。
 *
 * 食い違っていた点:
 *   - 本文のキーが API は `bodyText`、画面は `body`
 *   - 期間のキーが API は `startsAt` / `endsAt`、画面は `startAt` / `endAt`
 *   - 画面の「同じ相手には 1 日 1 回だけ返信する」は真偽値だが、
 *     API は再送間隔（秒）の `repeatIntervalSeconds`
 *
 * どちらを正にするか: **API の形を正とする**。再送間隔を秒で持っているのは
 * 「1 日 1 回」以外の運用（長期休暇で 1 週間に 1 回など）を後から足せるようにする
 * ためで、真偽値に丸めてしまうとその情報が API 側で失われる。画面が扱えるのは
 * 今のところ 2 値だけなので、変換をここに閉じ込めて責任を明示する。
 */
import type { Autoresponder } from './api-types.js';

/** API の下限は 3600 秒。「毎回返す」に一番近い値としてこれを使う。 */
const REPEAT_ALWAYS_SECONDS = 3600;
/** 1 日 1 回。 */
const REPEAT_ONCE_PER_DAY_SECONDS = 86400;

/** 画面が編集する形。API に無いキーは持たせない。 */
export interface AutoresponderDraft {
  enabled: boolean;
  subject: string;
  body: string;
  startAt: string | null;
  endAt: string | null;
  /** 同じ相手には 1 日 1 回だけ返す。 */
  oncePerDay: boolean;
}

export function toAutoresponderDraft(res: Autoresponder): AutoresponderDraft {
  return {
    enabled: res.enabled,
    subject: res.subject,
    body: res.bodyText,
    startAt: res.startsAt,
    endAt: res.endsAt,
    // 1 日以上の間隔なら「1 日 1 回」とみなす。
    oncePerDay: res.repeatIntervalSeconds >= REPEAT_ONCE_PER_DAY_SECONDS,
  };
}

export function fromAutoresponderDraft(draft: AutoresponderDraft): Autoresponder {
  return {
    enabled: draft.enabled,
    subject: draft.subject,
    bodyText: draft.body,
    // 差出人表示名はメールボックスの表示名を使うので画面では編集させない。
    fromDisplayName: null,
    startsAt: draft.startAt,
    endsAt: draft.endAt,
    repeatIntervalSeconds: draft.oncePerDay ? REPEAT_ONCE_PER_DAY_SECONDS : REPEAT_ALWAYS_SECONDS,
    // 自分宛（To/Cc に自分が入っている）に限る既定は変えない。
    // ML の同報に自動応答すると全員に飛ぶため。
    onlyToMyAddresses: true,
  };
}
